import { useTranslation } from 'react-i18next';
import {
  iconBookMenuCourses,
  iconCheckMenuCourses,
  iconCurriculumMenuCourses,
  iconTargetingMenuCourses,
} from '../../assets';
import { useTheme } from '../../theme';

/** Itens do menu: id 1 = CreateCourseStepOne, 2 = CreateCourseStepTwo, 3 = CreateCourseStepThree (Targeting & Publish) */
const STEPS = [
  { id: 1, key: 'step_basic_info', iconUrl: iconBookMenuCourses },
  { id: 2, key: 'step_curriculum', iconUrl: iconCurriculumMenuCourses },
  { id: 3, key: 'step_targeting', iconUrl: iconTargetingMenuCourses },
] as const;

export interface CreateCourseSidebarProps {
  currentStep: number;
  completionPercent?: number;
  /** @deprecated Não usado: passo atual sempre exibe como ativo, nunca como concluído */
  step3ReachedViaNext?: boolean;
  onStepClick?: (step: number) => void;
  /** Identificador de teste para o sidebar do Create Course */
  dataE2eSidebar?: string;
}

export function CreateCourseSidebar({
  currentStep,
  completionPercent = 0,
  step3ReachedViaNext: _step3ReachedViaNext = false,
  onStepClick,
  dataE2eSidebar,
}: CreateCourseSidebarProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const sidebarStyle: React.CSSProperties = {
    width: theme.palette.createCourse.sidebar.cardWidth,
    flexShrink: 0,
    minHeight: 0,
    padding: theme.spacing.lg,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.sm,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  };

  const stepRowStyle = (
    active: boolean,
    completed: boolean,
  ): React.CSSProperties => {
    const sc = theme.palette.createCourse.sidebar;
    return {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
      padding: theme.spacing.sm,
      marginBottom: theme.spacing.xs,
      borderRadius: theme.shape.borderRadius.sm,
      width: '100%',
      boxSizing: 'border-box',
      ...(completed && {
        backgroundColor: sc.stepCompletedBg,
        border: `1px solid ${sc.stepCompletedBorder}`,
      }),
      ...(active && !completed && {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        backgroundColor: sc.stepActiveBg,
      }),
      ...(!active && !completed && {
        borderLeft: '4px solid transparent',
        backgroundColor: 'transparent',
      }),
    };
  };

  const iconWrapperStyle = (
    active: boolean,
    completed: boolean,
  ): React.CSSProperties => {
    const sc = theme.palette.createCourse.sidebar;
    return {
      width: 40,
      height: 40,
      borderRadius: '50%',
      backgroundColor: completed
        ? sc.stepCompletedCircle
        : active
          ? theme.palette.primary.main
          : theme.palette.background.default,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      border:
        completed || active
          ? 'none'
          : `2px solid ${theme.palette.border.main}`,
    };
  };

  const progressBoxStyle: React.CSSProperties = {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.createCourse.sidebar.progressBoxBg,
    borderRadius: theme.shape.borderRadius.sm,
    border: 'none',
  };

  const progressLabelStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.createCourse.sidebar.progressLabelText,
    marginBottom: theme.spacing.xs,
  };

  const progressSublabelStyle: React.CSSProperties = {
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.createCourse.sidebar.progressValueText,
    marginBottom: theme.spacing.sm,
  };

  const progressPercentStyle: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.createCourse.sidebar.progressValueText,
    textAlign: 'right' as const,
    marginBottom: theme.spacing.xs,
  };

  const progressBarBg: React.CSSProperties = {
    height: theme.palette.createCourse.sidebar.progressBarHeight,
    borderRadius: theme.shape.borderRadius.full,
    backgroundColor: theme.palette.createCourse.sidebar.progressBarBg,
    overflow: 'hidden',
  };

  const progressBarFill: React.CSSProperties = {
    height: '100%',
    width: `${Math.min(100, Math.max(0, completionPercent))}%`,
    backgroundColor: theme.palette.createCourse.sidebar.progressBarFill,
    borderRadius: theme.shape.borderRadius.full,
    transition: 'width 0.2s ease',
  };

  return (
    <aside style={sidebarStyle} data-e2e={dataE2eSidebar}>
      {STEPS.map(({ id, key, iconUrl }) => {
        const active = currentStep === id;
        const completed = id < currentStep;
        /** Só steps anteriores ao atual usam estilo concluído (verde); o passo atual sempre fica ativo (amarelo) */
        const useCompletedStyle = completed;
        const sc = theme.palette.createCourse.sidebar;
        return (
          <div
            key={id}
            role={onStepClick ? 'button' : undefined}
            tabIndex={onStepClick ? 0 : undefined}
            onClick={onStepClick ? () => onStepClick(id) : undefined}
            onKeyDown={
              onStepClick
                ? e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onStepClick(id);
                    }
                  }
                : undefined
            }
            style={{
              ...stepRowStyle(active, useCompletedStyle),
              ...(onStepClick && {
                cursor: 'pointer',
              }),
            }}
          >
            <div style={iconWrapperStyle(active, useCompletedStyle)}>
              <img
                src={completed ? iconCheckMenuCourses : iconUrl}
                alt=""
                width={20}
                height={20}
                style={{
                  objectFit: 'contain',
                  ...(useCompletedStyle
                    ? { filter: 'brightness(0) invert(1)', opacity: 1 }
                    : active
                      ? { filter: 'brightness(0) invert(1)', opacity: 1 }
                      : {
                          filter: 'brightness(0) saturate(100%)',
                          opacity: 0.6,
                        }),
                }}
                aria-hidden
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: theme.typography.fontSize.xs,
                  color: useCompletedStyle
                    ? sc.stepCompletedTitle
                    : active
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  fontWeight: theme.typography.fontWeight.semibold,
                  marginBottom: theme.palette.createCourse.sidebar.stepLabelMarginBottom,
                  letterSpacing: '0.02em',
                }}
              >
                {t('courses.create.step_number', { number: id }).toUpperCase()}
              </div>
              <div
                style={{
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.semibold,
                  color: useCompletedStyle
                    ? sc.stepCompletedSubtitle
                    : active
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary,
                }}
              >
                {t(`courses.create.${key}`)}
              </div>
            </div>
          </div>
        );
      })}
      <div style={progressBoxStyle}>
        <div style={progressLabelStyle}>
          {t('courses.create.course_progress')}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing.xs,
          }}
        >
          <span style={progressSublabelStyle}>
            {t('courses.create.completion_label')}
          </span>
          <span style={progressPercentStyle}>{completionPercent}%</span>
        </div>
        <div style={progressBarBg}>
          <div style={progressBarFill} />
        </div>
      </div>
    </aside>
  );
}
